import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Box, Button, Typography, Grid2 } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import UseNavigation from '@/src/hooks/useNavigation';
import { RootState } from '@/src/datastore/store';
import AttributesConfig, { IAttributeDef } from '@/src/config/AttributesConfig';
import AppConfig from '@/src/config/AppConfig';
import CustomTablePaginationActions from '@/src/components/common/CustomTablePaginationActions';
import NoAuthorization from '@/src/components/common/NoAuthorization';
import DynamicInput from '@/src/components/common/DynamicInput';
import {
  UI_TYPE_INPUT_TYPE_MAP,
  INPUT_FIELD_TYPE_MAP,
  ILooseObject,
  IAttributeOptions
} from '@/src/types/common.interface';
import lightTheme from '@/src/styles/themes/lightTheme';
import AllAppLevelCredentialsService from '@/src/services/all-app-Level-Credentials/allAppLevelCredentialsService';
import FormattedUILabel from '@/src/components/common/FormattedUILabel';

const AppLevelCredentials = () => {
  const [extraFieldConfig, setExtraFieldConfig] = useState<ILooseObject>({});
  const [tableDetails, setTableDetails] = useState<ILooseObject>({ columns: [], rows: [] });
  const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);

  const {
    control,
    formState: { dirtyFields, errors },
    watch,
    reset,
    getValues,
    clearErrors
  } = useForm({
    mode: 'onSubmit',
    defaultValues: AttributesConfig.All_APP_LEVEL_CREDENTIALS_CONFIG_LIST_INPUT?.reduce(
      (acc: ILooseObject, attribute: IAttributeDef) => {
        if (attribute?.name === 'search_context') {
          acc[attribute?.name] = [];
        } else {
          acc[attribute?.name] = '';
        }
        return acc;
      },
      {}
    )
  });
  const watchedValues: ILooseObject = watch();
  const getInputValues: ILooseObject = getValues();

  const navigate = UseNavigation();
  const userAppPermissions: ILooseObject = useSelector(
    (state: RootState) => state?.userDetails?.OptionsAndPermissions?.appPermissions
  );
  const getAppPermissions = (app_key?: string) => {
    return Object.entries(userAppPermissions as Record<string, string[]>)?.reduce(
      (acc: string[], [key, permissions]: [string, string[]]) => {
        if (app_key) {
          if (key === app_key) {
            return Array.from(new Set([...acc, ...permissions]));
          } else {
            return acc;
          }
        }
        return Array.from(new Set([...acc, ...permissions]));
      },
      []
    );
  };
  const userApplicationsOptions: IAttributeOptions[] = Object.values(
    useSelector((state: RootState) => state?.userDetails?.OptionsAndPermissions)
      ?.applicationsOptions as IAttributeOptions[]
  )
    ?.map((application: IAttributeOptions) => application)
    ?.filter((application: IAttributeOptions) => {
      return getAppPermissions(application.value as string)?.includes('api_config_view');
    });

  const getAppLevelCredentialList = async () => {
    try {
      setIsLoadingTable(true);
      const results = await AllAppLevelCredentialsService.getAllAppLevelCredentialsList({
        filters: getInputValues
      });

      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
      } else if (!results.error.code) {
        setTableDetails({
          columns: results.columns,
          rows: results.rows
        });
      }
    } catch (error) {
      enqueueSnackbar(error?.toString(), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
      setTableDetails({
        columns: [],
        rows: []
      });
    } finally {
      setIsLoadingTable(false);
    }
  };

  const getAvailableOptions = (attributeField: ILooseObject, extraFieldConfig?: ILooseObject) => {
    const availableOptions =
      extraFieldConfig?.[attributeField?.name]?.options ?? attributeField?.options ?? [];
    return availableOptions;
  };

  useEffect(() => {
    if (dirtyFields?.app_key && dirtyFields?.search_context) {
      getAppLevelCredentialList();
    }
  }, [JSON.stringify(watchedValues), JSON.stringify(dirtyFields)]);

  useEffect(() => {
    if (userApplicationsOptions?.length > 0) {
      setExtraFieldConfig((preValue) => ({
        ...preValue,
        app_key: { ...preValue?.app_key, options: userApplicationsOptions }
      }));
    }
  }, []);

  if (!getAppPermissions()?.includes('x_api_key_config_view')) {
    return <NoAuthorization />;
  }

  return (
    <Box display="flex" flexDirection="column" rowGap={2}>
      <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" color="primary.dark">
          App Level Credentials
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          size="small"
          sx={{ textTransform: 'none' }}
          disabled={!getAppPermissions()?.includes('x_api_key_config_view')}
          onClick={() => {
            navigate.navigateTo('/app-level-credentials/add-app-level-credentials');
          }}
        >
          Create App Level Credentials
        </Button>
      </Box>
      <Box
        border={1}
        borderColor={lightTheme.palette.grey[300]}
        borderRadius={3}
        sx={{ p: 2, display: 'flex', flexDirection: 'column', rowGap: 1 }}
      >
        <Grid2
          container
          component="form"
          display="flex"
          flexDirection="row"
          columnGap={2}
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          {AttributesConfig.All_APP_LEVEL_CREDENTIALS_CONFIG_LIST_INPUT?.map(
            (attributeField: IAttributeDef) => {
              const type = UI_TYPE_INPUT_TYPE_MAP[attributeField?.ui_type?.toUpperCase()];
              const inputType = INPUT_FIELD_TYPE_MAP[attributeField?.value_type?.toUpperCase()];
              const isRequired = !!attributeField?.is_mandatory;
              const isVisible = !!attributeField?.is_visible;
              if (!isVisible) {
                return null;
              }
              const availableOptions = getAvailableOptions(attributeField, extraFieldConfig);

              return (
                <Grid2
                  display="flex"
                  flexDirection="column"
                  rowGap={0.5}
                  key={attributeField?.attribute_id}
                  size={{ xs: 4 }}
                >
                  <FormattedUILabel
                    uiLabel={attributeField?.ui_label}
                    isRequired={attributeField?.is_mandatory}
                  />
                  <Controller
                    name={attributeField?.name as string}
                    control={control}
                    render={({ field: { onChange, onBlur, value, name } }) => (
                      <DynamicInput
                        id={attributeField?.attribute_id}
                        label=""
                        type={type}
                        inputType={inputType}
                        required={isRequired}
                        placeholder={attributeField?.placeholder}
                        value={
                          type === UI_TYPE_INPUT_TYPE_MAP.CHECKBOX
                            ? Array.isArray(value)
                              ? value
                              : []
                            : (value ?? '')
                        }
                        options={availableOptions}
                        size="small"
                        helperText={(errors[name]?.message as string) ?? ' '}
                        hasError={!!(errors[name]?.message as string)}
                        onChange={onChange}
                        onBlur={onBlur}
                        disableClearable={true}
                      />
                    )}
                  />
                </Grid2>
              );
            }
          )}
          <Box display="flex" flexDirection="row" columnGap={2}>
            <Button
              variant="outlined"
              size="small"
              sx={{ textTransform: 'none' }}
              onClick={() => {
                reset();
                clearErrors();
              }}
              disabled={!(dirtyFields && Object.keys(dirtyFields)?.length > 0)}
            >
              Reset
            </Button>
          </Box>
        </Grid2>
      </Box>
      {Object.keys(dirtyFields)?.length > 1 && (
        <Box
          border={1}
          borderColor={lightTheme.palette.grey[300]}
          borderRadius={3}
          sx={{ p: 2, display: 'flex', flexDirection: 'column', rowGap: 1 }}
        >
          {getAppPermissions(watchedValues?.application)?.includes('x_api_key_config_view') ? (
            <DataGrid
              getRowId={(row) => row?.client_id}
              rows={tableDetails.rows}
              columns={tableDetails.columns}
              disableRowSelectionOnClick
              disableColumnSelector
              disableColumnMenu
              disableDensitySelector
              slots={{ toolbar: GridToolbar }}
              getRowHeight={() => 'auto'}
              sx={{
                '& .MuiDataGrid-columnHeaderTitle': {
                  textOverflow: 'clip',
                  whiteSpace: 'break-spaces',
                  lineHeight: 1.15,
                  fontWeight: 600
                },
                '& .MuiDataGrid-row': {
                  minHeight: '52px !important'
                },
                '& .MuiDataGrid-cell': {
                  whiteSpace: 'normal',
                  lineHeight: 'normal',
                  padding: '8px 16px'
                },
                '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-cell:focus': {
                  outline: 'none'
                },
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none'
                },
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none'
                },
                '& .MuiDataGrid-main': {
                  overflow: 'unset'
                },
                '& .MuiDataGrid-columnHeaders': {
                  position: 'sticky',
                  top: 63,
                  zIndex: 1
                },
                '& .MuiDataGrid-container--top': {
                  zIndex: 1
                },
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: lightTheme.palette.grey[100]
                }
              }}
              slotProps={{
                pagination: {
                  ActionsComponent: CustomTablePaginationActions
                },
                toolbar: {
                  csvOptions: { disableToolbarButton: true },
                  printOptions: { disableToolbarButton: true },
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 }
                }
              }}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 20, page: 0 }
                }
              }}
              loading={isLoadingTable}
              pageSizeOptions={[20, 50, 100]}
            />
          ) : (
            <NoAuthorization />
          )}
        </Box>
      )}
    </Box>
  );
};

export default AppLevelCredentials;
